import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BoltIcon from '@mui/icons-material/Bolt';

import api from '../../../../services/api';
import { useNotify } from '../../../../components/Datatable/NotifyProvider';
import { SUPERFICIE } from './estilo';

const ROTA = '/admin/snapshot';

// Os seis estagios do validador, na ordem em que rodam. Mostrar QUAL barrou
// importa: "allowlist" significa "comando desconhecido, peça o cadastro";
// "denylist" significa "esse comando é destrutivo, não insista". Sem isso o
// operador só vê "negado" e abre chamado.
const ESTAGIO = {
  normalize: { label: 'vazio', desc: 'Comando vazio ou só espaços' },
  length: { label: 'tamanho', desc: 'Comando longo demais' },
  metachars: { label: 'metacaractere', desc: 'Caractere que encadeia ou redireciona comandos' },
  denylist: { label: 'destrutivo', desc: 'Verbo destrutivo explícito' },
  allowlist: { label: 'fora do catálogo', desc: 'Não é um comando de leitura conhecido' },
  pipe: { label: 'pipe', desc: 'Filtro depois do | não permitido' },
  duplicado: { label: 'duplicado', desc: 'Já existe no catálogo' },
};

/** Uma caixa de comando, com o veredito do vendor logo abaixo. */
function CaixaVendor({ vendor, valor, aoMudar, veredito, validando, erroServidor }) {
  const ok = veredito?.allowed;
  const negado = veredito && !veredito.allowed;
  const estagio = ESTAGIO[erroServidor?.stage || veredito?.stage];

  const cor = erroServidor || negado ? '#c0392b' : ok ? '#16a085' : '#cbd5e1';

  return (
    <Box sx={{ mb: 1.5 }}>
      <TextField
        fullWidth
        size="small"
        label={vendor}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        placeholder={
          vendor === 'HUAWEI' ? 'display ...'
            : vendor === 'JUNIPER' ? 'show ... | no-more'
              : 'show ...'
        }
        InputProps={{
          sx: { fontFamily: 'monospace', fontSize: 13 },
          endAdornment: validando
            ? <CircularProgress size={13} />
            : ok
              ? <CheckCircleIcon sx={{ fontSize: 18, color: '#16a085' }} />
              : (negado || erroServidor)
                ? <BlockIcon sx={{ fontSize: 18, color: '#c0392b' }} />
                : null,
        }}
        sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: cor } }}
      />

      {(erroServidor || negado) && (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5, ml: 0.5 }}
          flexWrap="wrap" useFlexGap>
          {estagio && (
            <Tooltip title={estagio.desc}>
              <Chip size="small" label={estagio.label}
                sx={{ height: 18, bgcolor: '#c0392b', color: '#fff' }} />
            </Tooltip>
          )}
          <Typography variant="caption" sx={{ color: '#991b1b' }}>
            {erroServidor?.msg || veredito?.reason}
          </Typography>
        </Stack>
      )}

      {ok && veredito.is_heavy && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, ml: 0.5 }}>
          <BoltIcon sx={{ fontSize: 14, color: '#d97706' }} />
          <Typography variant="caption" sx={{ color: '#92400e' }}>
            saída grande — vai rodar com timeout maior
          </Typography>
        </Stack>
      )}

      {ok && (veredito.warnings || []).map((w, i) => (
        <Stack key={i} direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, ml: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 14, color: '#d97706' }} />
          <Typography variant="caption" sx={{ color: '#92400e' }}>{w}</Typography>
        </Stack>
      ))}
    </Box>
  );
}

/**
 * Cadastro de comando livre (nível 2): UM conceito, uma grafia por vendor.
 *
 * A versão anterior pedia um comando solto por vez, sem categoria e sem relação
 * com o equivalente dos outros equipamentos. Na prática o operador quer "resumo
 * de BGP" e tinha que cadastrar quatro vezes — e no catálogo viravam quatro
 * linhas que ninguém relacionava.
 *
 * A validação enquanto se digita existe porque nenhum mecanismo nativo de
 * equipamento consegue fazer isso: TACACS+, parser view e CLI profile todos
 * exigem a sessão já estabelecida, então sem esta tela o operador só descobriria
 * que o comando é proibido depois de esperar o SSH conectar.
 *
 * O servidor valida de novo no POST, sem confiar neste veredito. A tela é
 * conveniência, não é a trava.
 */
export default function ComandoLivreDialog({ aberto, empresa, onFechar, onCriado }) {
  const notify = useNotify();

  const [opcoes, setOpcoes] = useState({ categories: [], vendors: [] });
  const [label, setLabel] = useState('');
  const [categoria, setCategoria] = useState('');
  const [timeoutS, setTimeoutS] = useState(60);
  const [textos, setTextos] = useState({});
  const [vereditos, setVereditos] = useState({});
  const [validando, setValidando] = useState({});
  const [errosServidor, setErrosServidor] = useState({});
  const [salvando, setSalvando] = useState(false);

  const debounces = useRef({});

  useEffect(() => {
    if (!aberto) {
      setLabel(''); setCategoria(''); setTextos({});
      setVereditos({}); setErrosServidor({}); setTimeoutS(60);
      return;
    }
    api.get(`${ROTA}/command/options`)
      .then((r) => setOpcoes(r.data?.data || { categories: [], vendors: [] }))
      .catch(() => { });
  }, [aberto]);

  /** Valida uma caixa só, com atraso — validar a cada tecla geraria uma
   *  requisição por caractere. */
  const validar = useCallback(async (vendor, texto) => {
    if (!texto.trim()) {
      setVereditos((v) => ({ ...v, [vendor]: null }));
      return;
    }
    setValidando((v) => ({ ...v, [vendor]: true }));
    try {
      const res = await api.post(`${ROTA}/command/validate`, {
        commands: [texto], vendor, empresa,
      });
      const r = res.data?.data?.results?.[0] || null;
      setVereditos((v) => ({ ...v, [vendor]: r }));
    } catch {
      setVereditos((v) => ({ ...v, [vendor]: null }));
    } finally {
      setValidando((v) => ({ ...v, [vendor]: false }));
    }
  }, [empresa]);

  const mudar = (vendor) => (texto) => {
    setTextos((t) => ({ ...t, [vendor]: texto }));
    // O erro do servidor deixa de valer assim que o texto muda.
    setErrosServidor((e) => ({ ...e, [vendor]: null }));

    if (debounces.current[vendor]) clearTimeout(debounces.current[vendor]);
    debounces.current[vendor] = setTimeout(() => validar(vendor, texto), 400);
  };

  const preenchidos = useMemo(
    () => Object.entries(textos).filter(([, t]) => (t || '').trim()),
    [textos]
  );

  const algumNegado = preenchidos.some(
    ([v]) => (vereditos[v] && !vereditos[v].allowed) || errosServidor[v]
  );

  const podeSalvar =
    label.trim() && categoria && preenchidos.length > 0 && !algumNegado && !salvando;

  const salvar = async () => {
    setSalvando(true);
    setErrosServidor({});
    try {
      const commands = Object.fromEntries(preenchidos.map(([v, t]) => [v, t.trim()]));
      const res = await api.post(`${ROTA}/command/free`, {
        empresa,
        label: label.trim(),
        category: categoria,
        timeout_s: Number(timeoutS) || 60,
        commands,
      });

      if (res.data?.errors?.length) {
        // Tudo ou nada: o servidor não gravou NADA. Marcar cada caixa é o que
        // deixa isso claro — uma mensagem genérica faria o operador achar que
        // parte entrou.
        const porCampo = {};
        res.data.errors.forEach((e) => { porCampo[e.field] = e; });
        setErrosServidor(porCampo);
        const geral = res.data.errors.find((e) => !commands[e.field]);
        notify.setNotify({
          open: true,
          severity: 'error',
          message: geral ? geral.msg : 'Nada foi cadastrado — corrija os comandos marcados',
        });
        return;
      }

      const d = res.data.data;
      notify.setNotify({
        open: true,
        severity: 'success',
        message: `“${d.label}” cadastrado em ${d.commands.length} equipamento(s)`,
      });
      onCriado?.();
      onFechar();
    } catch {
      notify.setNotify({ open: true, severity: 'error', message: 'Erro ao cadastrar' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth="md" fullWidth PaperProps={{ sx: SUPERFICIE }}>
      <DialogTitle sx={{ pb: 1 }}>
        Novo comando
        <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
          Um conceito, uma grafia por equipamento
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Só entram comandos de <strong>coleta</strong>. Qualquer coisa que configure,
          apague ou reinicie é bloqueada — e o comando é validado de novo no servidor
          antes de gravar.
        </Alert>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            autoFocus
            label="O que este comando mostra"
            placeholder="Ex.: Resumo de BGP"
            size="small"
            sx={{ flex: 1 }}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            helperText="É o nome que aparece no catálogo"
          />
          <TextField
            select label="Categoria" size="small" sx={{ width: 170 }}
            value={categoria} onChange={(e) => setCategoria(e.target.value)}
            helperText="Agrupa no catálogo"
          >
            {opcoes.categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField
            label="Timeout (s)" size="small" type="number" sx={{ width: 120 }}
            value={timeoutS} onChange={(e) => setTimeoutS(e.target.value)}
          />
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1.5 }}>
          Escreva o equivalente em cada equipamento. Pode deixar em branco o que
          não se aplica — só o que estiver preenchido é cadastrado.
        </Typography>

        {opcoes.vendors.map((v) => (
          <CaixaVendor
            key={v.vendor}
            vendor={v.vendor}
            valor={textos[v.vendor] || ''}
            aoMudar={mudar(v.vendor)}
            veredito={vereditos[v.vendor]}
            validando={validando[v.vendor]}
            erroServidor={errosServidor[v.vendor]}
          />
        ))}

        {!opcoes.vendors.length && <CircularProgress size={18} />}
      </DialogContent>

      <DialogActions>
        <Typography variant="caption" sx={{ color: '#64748b', mr: 'auto', ml: 1 }}>
          {preenchidos.length
            ? `${preenchidos.length} equipamento(s) preenchido(s)`
            : 'nenhum equipamento preenchido'}
        </Typography>
        <Button onClick={onFechar}>Cancelar</Button>
        <Button variant="contained" disabled={!podeSalvar} onClick={salvar}>
          {salvando ? 'Cadastrando…' : 'Cadastrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
